import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  Users, 
  MessageSquare, 
  ShieldAlert, 
  Flag, 
  Check, 
  X, 
  Sparkles, 
  ExternalLink,
  Lock,
  Trash2,
  Activity,
  Award,
  BookOpen,
  CalendarDays,
  Target,
  LogOut,
  Flame,
  ShieldCheck,
  TrendingUp,
  Inbox,
  Clock,
  Briefcase,
  Quote,
  Zap,
  Globe,
  Star,
  Trophy,
  Hand,
  UserCheck,
  Scale,
  AlertTriangle,
  FolderMinus,
  RefreshCw,
  Terminal,
  LineChart as LucideLineChart,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "@/components/theme-provider";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODERATION_INSIGHTS = [
  {
    text: "Verify candidate roll numbers against the registry before granting full credentials.",
    author: "System Sentinel",
  },
  {
    text: "Intercepted mentorship requests help maintain platform authenticity and prevent spam.",
    author: "Core Security",
  },
  {
    text: "Community soft-deletes completely hide objectionable content to keep feeds clean.",
    author: "Admin Policy",
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { getToken, isLoaded, isSignedIn, signOut } = useAuth();

  const [activeTab, setActiveTab] = useState("verification");
  const [stats, setStats] = useState({ total_users: 0, pending_verifications: 0, active_sessions: 0 });
  const [loading, setLoading] = useState(true);
  const [insightIndex, setInsightIndex] = useState(0);

  // User and Analytics data
  const [user, setUser] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [domains, setDomains] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [topEmployers, setTopEmployers] = useState([]);

  // Administrative Streams Data
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [mentorshipRequests, setMentorshipRequests] = useState([]);
  const [reportedPosts, setReportedPosts] = useState([]);
  const [profileReports, setProfileReports] = useState([]);

  // Mock Admin Options
  const [globalModerationLive, setGlobalModerationLive] = useState(true);
  const [readOnlyLock, setReadOnlyLock] = useState(false);

  // Fetch Administrative Streams Data
  const fetchStreamsData = useCallback(async () => {
    try {
      const token = await getToken();
      
      // 0. Fetch User profile
      const userRes = await axios.get(
        `${API_URL}/api/auth/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(userRes.data);

      // 0.5 Fetch Alumni Growth Trends
      const trendsRes = await axios.get(
        `${API_URL}/api/analytics/overview/trends`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrendsData(trendsRes.data || []);

      // Fetch Domain Distribution
      const domainsRes = await axios.get(
        `${API_URL}/api/analytics/industry/domains`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomains(domainsRes.data || []);

      // Fetch Role Distribution
      const rolesRes = await axios.get(
        `${API_URL}/api/analytics/industry/roles`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoles(rolesRes.data || []);

      // Fetch Employment Status
      const statusRes = await axios.get(
        `${API_URL}/api/analytics/employment/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatusData(statusRes.data || []);

      // Fetch Top Hiring Companies
      const employersRes = await axios.get(
        `${API_URL}/api/analytics/top-employers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTopEmployers(employersRes.data || []);

      // 1. Stats overview
      const statsRes = await axios.get(
        `${API_URL}/api/admin/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(statsRes.data);

      // 2. Pending registration requests
      const regRes = await axios.get(
        `${API_URL}/api/admin/registration-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrationRequests(regRes.data);

      // 3. Mentorship requests (all scoped requests)
      const mentorRes = await axios.get(
        `${API_URL}/api/mentorship/requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMentorshipRequests(mentorRes.data);

      // 4. Flagged/Reported posts
      const reportedRes = await axios.get(
        `${API_URL}/api/admin/reported-posts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportedPosts(reportedRes.data);

      // 5. Alumni profile false info reports
      const falseInfoRes = await axios.get(
        `${API_URL}/api/admin/false-info-reports`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileReports(falseInfoRes.data);

    } catch (err) {
      console.error("Failed to load admin streams:", err);
      toast.error("Error loading administration queues");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      navigate("/", { replace: true });
      return;
    }

    fetchStreamsData();
  }, [isLoaded, isSignedIn, fetchStreamsData, navigate]);

  // Tip Slider Logic
  useEffect(() => {
    const timer = setInterval(() => {
      setInsightIndex((prev) => (prev + 1) % MODERATION_INSIGHTS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/", { replace: true });
    } catch {
      toast.error("Logout failed");
    }
  };

  /* ========================================================
     Action Handlers
     ======================================================== */
  
  const handleRegistrationAction = async (userId, action) => {
    try {
      const token = await getToken();
      await axios.post(
        `${API_URL}/api/admin/registration-requests/${userId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User registration ${action}d successfully`);
      fetchStreamsData();
    } catch (err) {
      toast.error(`Failed to ${action} user registration`);
    }
  };

  const handleMentorshipAction = async (requestId, action) => {
    try {
      const token = await getToken();
      await axios.put(
        `${API_URL}/api/admin/mentorship/requests/${requestId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Mentorship request approved & forwarded to Alumni`);
      fetchStreamsData();
    } catch (err) {
      toast.error(`Failed to approve mentorship request`);
    }
  };

  const handleDeleteMentorshipLog = async (requestId) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${API_URL}/api/admin/mentorship/requests/${requestId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mentorship request log deleted successfully");
      fetchStreamsData();
    } catch (err) {
      toast.error("Failed to delete mentorship log");
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${API_URL}/api/admin/posts/${postId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Post soft-deleted successfully");
      fetchStreamsData();
    } catch (err) {
      toast.error("Failed to soft-delete post");
    }
  };

  const handleResolveProfileReport = async (reportId, action) => {
    try {
      const token = await getToken();
      await axios.post(
        `${API_URL}/api/admin/reports/${reportId}/resolve`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Profile report resolved with action: ${action.replace('_', ' ')}`);
      fetchStreamsData();
    } catch (err) {
      toast.error("Failed to resolve profile report");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="relative flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-t-[#002147] dark:border-t-blue-500 border-r-transparent border-l-transparent border-b-transparent rounded-full animate-spin absolute" />
          <Activity className="w-6 h-6 text-[#002147] dark:text-blue-400 animate-pulse" />
        </div>
        <p className="text-[#002147] dark:text-slate-300 font-serif font-black tracking-widest uppercase text-xs mt-12 animate-pulse">Syncing Campus Administration Ledger...</p>
      </div>
    );
  }

  if (!isSignedIn) return null;

  // Progression Meter Calculations
  const SYSTEM_GOAL = 100;
  const currentModeratedSessions = stats.active_sessions || 0;
  const progressPercent = Math.min((currentModeratedSessions / SYSTEM_GOAL) * 100, 100);

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-[#020617] text-slate-900 dark:text-slate-100 pb-24 transition-colors duration-300 relative overflow-hidden font-sans">
      {/* Dynamic Background Blurs */}
      <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] bg-blue-400/10 dark:bg-blue-900/10 rounded-full blur-[10rem] pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-[35rem] h-[35rem] bg-indigo-400/5 dark:bg-indigo-900/5 rounded-full blur-[12rem] pointer-events-none" />

      {/* HEADER */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-100 dark:border-slate-900 bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-tr from-[#002147] to-[#0a3d62] dark:from-blue-600 dark:to-cyan-500 rounded-xl flex items-center justify-center text-white font-serif font-black text-xl shadow-lg shadow-blue-500/10">
              A
            </div>
            <div>
              <h1 className="font-serif text-xl font-bold text-[#002147] dark:text-white tracking-tight leading-none">
                AlumConnect
              </h1>
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Central Admin Console</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/community")}
              className="rounded-full w-9 h-9 border-slate-200 dark:border-slate-800 dark:bg-slate-900 flex items-center justify-center p-0 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
              title="Community Feed"
            >
              <Users className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-500 dark:text-slate-400 hover:text-red-500 font-bold text-xs uppercase tracking-widest transition-colors"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-12 relative z-10">
        
        {/* 🚀 ADMINISTRATIVE ACTION HUB */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-stretch">
          <div className="lg:col-span-12">
             <Badge className="bg-[#002147]/5 dark:bg-blue-500/10 text-[#002147] dark:text-blue-400 border-none px-4 py-1.5 font-black uppercase tracking-[0.25em] text-[10px] w-fit rounded-full">
              Administrative Command Center Active
            </Badge>
          </div>
          
          <div className="lg:col-span-5 flex flex-col justify-center">
            <h2 className="font-serif text-5xl sm:text-6xl font-black text-[#002147] dark:text-white mb-6 tracking-tighter leading-[0.9]">
              Platform<br/>
              <span className="text-[#002147]/40 dark:text-slate-500">Overview</span>
            </h2>
            <div className="flex flex-wrap items-center gap-6 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-[0.15em]">
              <span className="flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                Full System Domain
              </span>
              <span className="flex items-center gap-2.5">
                <Clock className="w-5 h-5 text-orange-400 animate-pulse" />
                Live Status Tracked
              </span>
            </div>
          </div>

          {/* Security Insights Slider */}
          <div className="lg:col-span-4">
            <div className="h-full bg-white dark:bg-slate-900/60 backdrop-blur-md border border-slate-200/60 dark:border-slate-800/80 rounded-[2rem] p-8 flex flex-col justify-between relative overflow-hidden group shadow-sm hover:shadow-md transition-all duration-500">
              <Quote className="absolute -top-4 -left-4 w-20 h-20 text-slate-100 dark:text-slate-800/20 rotate-12" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Security insight</p>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={insightIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="min-h-[100px]"
                  >
                    <p className="text-lg font-bold text-slate-800 dark:text-slate-200 leading-[1.4] mb-4 tracking-tight italic">
                      "{MODERATION_INSIGHTS[insightIndex].text}"
                    </p>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#002147] dark:text-blue-400">
                      <span className="opacity-40">BY </span>{MODERATION_INSIGHTS[insightIndex].author}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="flex gap-1.5 mt-4">
                {MODERATION_INSIGHTS.map((_, i) => (
                  <div key={i} className={`h-1 rounded-full transition-all duration-500 ${i === insightIndex ? 'w-6 bg-[#002147] dark:bg-blue-500' : 'w-1.5 bg-slate-200 dark:bg-slate-800'}`} />
                ))}
              </div>
            </div>
          </div>

          {/* System Velocity Tracker */}
          <Card className="lg:col-span-3 bg-gradient-to-br from-[#002147] to-[#0d2a4a] dark:from-[#0f172a] dark:to-[#1e293b] text-white border-none shadow-xl p-8 rounded-[2rem] relative overflow-hidden group">
            <Sparkles className="absolute -top-8 -right-8 w-28 h-28 text-white/5 rotate-12 group-hover:scale-110 transition-transform duration-1000" />
            <div className="relative z-10 flex flex-col h-full justify-between">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">SYSTEM LOAD</p>
                  <span className="px-2.5 py-0.5 bg-white/10 rounded-full text-[9px] font-black tracking-widest uppercase">{stats.active_sessions || 0}/{SYSTEM_GOAL}</span>
                </div>
                <h3 className="text-2xl font-serif font-black mb-2 tracking-tight">Active Flux</h3>
              </div>
              <div className="mt-6">
                <div className="w-full h-2 bg-white/10 rounded-full mb-4 overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full relative"
                  />
                </div>
                <p className="text-[9px] font-bold text-white/40 leading-relaxed uppercase tracking-wider">
                  {progressPercent}% Platform Throughput Velocity
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* 📊 PLATFORM STATISTICS PULSE ROW */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-yellow-600">
                <Users className="w-5 h-5" />
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Total Users</p>
            </div>
            <p className="text-3xl font-serif font-black text-slate-900 dark:text-white tracking-tighter leading-none">{stats.total_users || 0}</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-orange-500">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Pending Approvals</p>
            </div>
            <p className="text-3xl font-serif font-black text-slate-900 dark:text-white tracking-tighter leading-none">{registrationRequests.length}</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-blue-500">
                <Activity className="w-5 h-5" />
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Active Sessions</p>
            </div>
            <p className="text-3xl font-serif font-black text-slate-900 dark:text-white tracking-tighter leading-none">{stats.active_sessions || 0}</p>
          </div>
        </section>

        {/* MAIN COLUMN SYSTEM GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* MASTER ADMIN CONTROL SIDEBAR (LEFT) */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <Card className="border-none bg-white/70 dark:bg-slate-900/50 backdrop-blur-xl shadow-xl shadow-slate-100/10 dark:shadow-none overflow-hidden transition-all duration-500 rounded-[2.5rem] border border-slate-100/80 dark:border-slate-800/60 relative">
              {/* Premium Glow Accents */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl pointer-events-none" />
              
              <CardContent className="p-8 relative z-10">
                <div className="flex flex-col items-center text-center">
                  
                  {/* Dynamic Circular Avatar with Rings */}
                  <div className="relative mb-5 group">
                    <div className="absolute -inset-1.5 bg-gradient-to-tr from-blue-500 to-indigo-500 rounded-full opacity-20 blur-md group-hover:opacity-30 transition-opacity duration-500" />
                    
                    <div className="relative w-24 h-24 p-1 bg-white dark:bg-slate-950 rounded-full shadow-lg">
                      <Avatar className="w-full h-full rounded-full border border-slate-100 dark:border-slate-800 overflow-hidden">
                        <AvatarImage className="object-cover" src={user?.picture} />
                        <AvatarFallback className="text-xl font-bold text-blue-600 dark:text-blue-400 bg-slate-55/30 dark:bg-slate-850 font-sans">
                          {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : "AD"}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="absolute bottom-0.5 right-0.5 w-6 h-6 bg-emerald-500 border-4 border-white dark:border-slate-950 rounded-full shadow-sm flex items-center justify-center">
                         <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                      </div>
                    </div>
                  </div>

                  {/* Refined Sans-serif Header */}
                  <div className="space-y-1 mb-8">
                    <div className="flex items-center justify-center gap-1">
                      <h4 className="text-lg font-bold text-slate-800 dark:text-white tracking-tight">
                        {user?.name || "Master Admin"}
                      </h4>
                      <ShieldCheck className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="text-slate-400 dark:text-slate-500 font-bold text-[9px] uppercase tracking-[0.25em]">
                      {user?.designation || "Platform Control Hub"}
                    </div>
                  </div>

                  {/* Admin Status Toggles */}
                  <div className="w-full space-y-3">
                    <div className={`p-0.5 rounded-2xl transition-all duration-300 ${globalModerationLive ? 'bg-emerald-500/5' : 'bg-slate-50 dark:bg-slate-800/10'}`}>
                      <div className="flex items-center justify-between p-3.5 bg-white dark:bg-slate-900 rounded-[1.2rem] shadow-sm border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg transition-colors ${globalModerationLive ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-800 text-slate-400'}`}>
                            <Zap className={`w-3.5 h-3.5 ${globalModerationLive ? 'animate-pulse' : ''}`} />
                          </div>
                          <div className="text-left">
                             <p className={`text-[8.5px] font-black uppercase tracking-wider ${globalModerationLive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-450'}`}>Mod Stream</p>
                             <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-tighter">{globalModerationLive ? "Active Moderation" : "Paused Moderation"}</p>
                          </div>
                        </div>
                        <Switch 
                          checked={globalModerationLive} 
                          onCheckedChange={setGlobalModerationLive} 
                          className="data-[state=checked]:bg-emerald-500"
                        />
                      </div>
                    </div>

                    <div className={`p-0.5 rounded-2xl transition-all duration-300 ${readOnlyLock ? 'bg-red-500/5' : 'bg-slate-50 dark:bg-slate-800/10'}`}>
                      <div className="flex items-center justify-between p-3.5 bg-white dark:bg-slate-900 rounded-[1.2rem] shadow-sm border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg transition-colors ${readOnlyLock ? 'bg-red-500/10 text-red-500' : 'bg-slate-50 dark:bg-slate-800 text-slate-400'}`}>
                            <Lock className={`w-3.5 h-3.5 ${readOnlyLock ? 'animate-pulse' : ''}`} />
                          </div>
                          <div className="text-left">
                             <p className={`text-[8.5px] font-black uppercase tracking-wider ${readOnlyLock ? 'text-red-500' : 'text-slate-450'}`}>Emergency Lock</p>
                             <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-tighter">Emergency read-only</p>
                          </div>
                        </div>
                        <Switch 
                          checked={readOnlyLock} 
                          onCheckedChange={setReadOnlyLock} 
                          className="data-[state=checked]:bg-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform Analytics Button */}
                <div className="w-full mt-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab(activeTab === "statistics" ? "verification" : "statistics")}
                    className={`w-full text-left p-0.5 rounded-2xl transition-all duration-300 active:scale-[0.98] ${
                      activeTab === "statistics"
                        ? 'bg-blue-500/10 shadow-[0_8px_20px_rgba(59,130,246,0.06)] border border-blue-500/20'
                        : 'bg-slate-50 dark:bg-slate-800/20 hover:bg-slate-100/50'
                    }`}
                  >
                    <div className="flex items-center justify-between p-3.5 bg-white dark:bg-slate-900 rounded-[1.2rem] border border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg transition-all ${
                          activeTab === "statistics"
                            ? 'bg-blue-500 text-white animate-pulse'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-400'
                        }`}>
                          <LucideLineChart className="w-3.5 h-3.5" />
                        </div>
                        <div className="text-left">
                           <p className={`text-[8.5px] font-black uppercase tracking-wider ${
                             activeTab === "statistics" ? 'text-blue-600 dark:text-blue-400' : 'text-slate-455'
                           }`}>Platform Statistics</p>
                           <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-tighter">
                             {activeTab === "statistics" ? "Viewing Active Analytics" : "Open System Analytics"}
                           </p>
                        </div>
                      </div>
                      <div className={`w-2 h-2 rounded-full transition-all ${
                        activeTab === "statistics" ? "bg-blue-500 animate-ping" : "bg-slate-200 dark:bg-slate-700"
                      }`} />
                    </div>
                  </button>
                </div>

                {/* System Security Pulse - Widgets Panel */}
                <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                      <Terminal className="w-4 h-4 text-blue-500" />
                      <p className="text-[9px] font-bold uppercase tracking-widest">Platform Core Gateway</p>
                    </div>
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-850 hover:bg-white dark:hover:bg-slate-900 transition-colors shadow-sm">
                      <span className="text-[10px] font-semibold text-slate-500">Database Core</span>
                      <Badge className="bg-emerald-500/10 hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 dark:bg-emerald-500/20 rounded-md border-none text-[8px] font-bold tracking-wider uppercase">Active</Badge>
                    </div>
                    <div className="flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-850 hover:bg-white dark:hover:bg-slate-900 transition-colors shadow-sm">
                      <span className="text-[10px] font-semibold text-slate-500">API Gateway</span>
                      <Badge className="bg-emerald-500/10 hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 dark:bg-emerald-500/20 rounded-md border-none text-[8px] font-bold tracking-wider uppercase">Operational</Badge>
                    </div>
                    <div className="flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-850 hover:bg-white dark:hover:bg-slate-900 transition-colors shadow-sm">
                      <span className="text-[10px] font-semibold text-slate-500">SMTP Server</span>
                      <Badge className="bg-amber-500/10 hover:bg-amber-500/10 text-amber-600 dark:bg-amber-400 dark:bg-amber-500/20 rounded-md border-none text-[8px] font-bold tracking-wider uppercase">Simulation</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* MAIN TRANSACTION COLUMN (RIGHT) */}
          <div className="lg:col-span-8 space-y-8">
            
            {/* Pill Tab Switch Controls */}
            <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-900 pb-5">
              {[
                { id: "verification", label: "Identity Verification", icon: UserCheck, count: registrationRequests.length },
                { id: "mentorship", label: "Mentorship Interception", icon: Scale, count: mentorshipRequests.filter(r => r.admin_approval_status === "pending_admin_approval").length },
                { id: "moderation", label: "Community Moderation", icon: AlertTriangle, count: reportedPosts.length },
                { id: "reports", label: "Profile Reports", icon: FolderMinus, count: profileReports.length },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-wider transition-all duration-200 flex items-center gap-2 ${
                    activeTab === tab.id
                      ? "bg-[#002147] dark:bg-slate-100 text-white dark:text-[#002147] shadow-md shadow-blue-500/5 scale-105"
                      : "bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.id ? "bg-amber-500 text-[#002147]" : "bg-[#002147] dark:bg-slate-800 text-white"
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  
                  {/* TAB: IDENTITY VERIFICATION */}
                  {activeTab === "verification" && (
                    <div className="space-y-4">
                      {registrationRequests.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-16 text-center border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-50/50 dark:bg-blue-500/5 rounded-full blur-2xl" />
                          <div className="relative z-10">
                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                              <Users className="w-6 h-6 text-slate-300 dark:text-slate-650" />
                            </div>
                            <h4 className="text-xl font-serif font-black text-slate-800 dark:text-white mb-2 tracking-tight">Ledger Clear</h4>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium max-w-xs mx-auto">All pending candidate registrations have been fully resolved.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {registrationRequests.map((req) => (
                            <div key={req.user_id} className="bg-white dark:bg-slate-900 rounded-[1.8rem] border border-slate-200/60 dark:border-slate-800/85 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
                              <div className="absolute top-0 left-0 h-full w-1 bg-amber-500" />
                              <div>
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider mb-2 ${req.role === 'alumni' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'}`}>
                                      {req.role}
                                    </span>
                                    <h3 className="font-serif font-bold text-xl text-[#002147] dark:text-white leading-tight">{req.name}</h3>
                                    <p className="text-slate-400 dark:text-slate-500 text-[11px] font-bold font-mono tracking-tight mt-0.5">{req.email}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50/50 dark:bg-slate-950/30 rounded-2xl p-4 mb-6 border border-slate-100 dark:border-slate-850 text-xs">
                                  <div className="flex flex-col">
                                    <span className="text-slate-400 font-medium text-[9px] uppercase tracking-wider">Department</span>
                                    <span className="text-slate-700 dark:text-slate-200 font-bold">{req.department}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-slate-400 font-medium text-[9px] uppercase tracking-wider">Grad Year</span>
                                    <span className="text-slate-700 dark:text-slate-200 font-bold font-mono">{req.graduation_year}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-slate-400 font-medium text-[9px] uppercase tracking-wider">Method</span>
                                    <span className="text-slate-700 dark:text-slate-200 font-bold uppercase tracking-wide">{req.verification_type?.replace('_', ' ')}</span>
                                  </div>
                                </div>

                                <div className="bg-slate-50/20 dark:bg-slate-900/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800 text-xs mb-6">
                                  <span className="text-slate-400 font-black uppercase tracking-wider text-[8px] block mb-1">Verification Detail:</span>
                                  <div className="text-slate-800 dark:text-slate-350 font-bold break-all font-mono">
                                    {req.verification_type === "college_id_card" ? (
                                      <a href={req.verification_value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline text-blue-600 dark:text-blue-400">
                                        View Attached Credential <ExternalLink className="w-3 h-3" />
                                      </a>
                                    ) : req.verification_value}
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-3">
                                <Button 
                                  onClick={() => handleRegistrationAction(req.user_id, "approve")}
                                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-600 dark:to-cyan-600 text-white font-bold h-10 rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/10"
                                >
                                  <Check className="w-4 h-4" /> Approve
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleRegistrationAction(req.user_id, "reject")}
                                  className="flex-1 border-slate-200 dark:border-slate-850 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold h-10 rounded-xl flex items-center justify-center gap-1.5"
                                >
                                  <X className="w-4 h-4" /> Reject
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: MENTORSHIP INTERCEPTION */}
                  {activeTab === "mentorship" && (
                    <div className="space-y-4">
                      {mentorshipRequests.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-16 text-center border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-50/50 dark:bg-blue-500/5 rounded-full blur-2xl" />
                          <div className="relative z-10">
                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                              <MessageSquare className="w-6 h-6 text-slate-300 dark:text-slate-650" />
                            </div>
                            <h4 className="text-xl font-serif font-black text-slate-800 dark:text-white mb-2 tracking-tight">Ledger Clear</h4>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium max-w-xs mx-auto">No mentorship sessions are actively awaiting administrative approval.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {mentorshipRequests.map((req) => (
                            <div key={req.request_id} className="bg-white dark:bg-slate-900 rounded-[1.8rem] border border-slate-200/60 dark:border-slate-800/85 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                              <div className="absolute top-0 left-0 h-full w-1 bg-[#002147]" />
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <span className={`px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider ${
                                    req.admin_approval_status === 'pending_admin_approval' 
                                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-500' 
                                      : req.admin_approval_status === 'approved_by_admin' 
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-500' 
                                      : 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-500'
                                  }`}>
                                    {req.admin_approval_status?.replace('_', ' ')}
                                  </span>
                                  <span className="text-slate-400 dark:text-slate-500 text-[10px] font-bold font-mono">ID: {req.request_id}</span>
                                </div>
                                
                                <h3 className="font-serif font-bold text-xl text-[#002147] dark:text-white mb-2 leading-tight">
                                  Mentorship Request: {req.topic}
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed mb-4 max-w-xl">{req.description}</p>
                                
                                <div className="grid grid-cols-2 gap-4 max-w-md text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-550 border-t border-slate-100 dark:border-slate-800 pt-3">
                                  <div>Student ID: <span className="text-slate-700 dark:text-slate-300 font-mono font-bold tracking-normal text-xs block mt-0.5">{req.student_id}</span></div>
                                  <div>Mentor ID: <span className="text-slate-700 dark:text-slate-300 font-mono font-bold tracking-normal text-xs block mt-0.5">{req.mentor_id}</span></div>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                                {req.admin_approval_status === "pending_admin_approval" && (
                                  <>
                                    <Button 
                                      onClick={() => handleMentorshipAction(req.request_id, "approve")}
                                      className="flex-1 md:flex-none bg-gradient-to-r from-blue-600 to-indigo-650 text-white font-bold px-4 h-10 rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/10 text-xs"
                                    >
                                      <Check className="w-3.5 h-3.5" /> Approve
                                    </Button>
                                    <Button 
                                      variant="outline"
                                      onClick={() => handleMentorshipAction(req.request_id, "reject")}
                                      className="flex-1 md:flex-none border-slate-200 dark:border-slate-850 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold px-4 h-10 rounded-xl flex items-center justify-center gap-1.5 text-xs"
                                    >
                                      <X className="w-3.5 h-3.5" /> Reject
                                    </Button>
                                  </>
                                )}
                                <Button 
                                  variant="outline"
                                  onClick={() => handleDeleteMentorshipLog(req.request_id)}
                                  className="flex-1 md:flex-none border-rose-200 dark:border-rose-950/40 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 hover:text-rose-600 font-bold px-4 h-10 rounded-xl flex items-center justify-center gap-1.5 text-xs"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Delete Log
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: COMMUNITY MODERATION */}
                  {activeTab === "moderation" && (
                    <div className="space-y-4">
                      {reportedPosts.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-16 text-center border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-50/50 dark:bg-blue-500/5 rounded-full blur-2xl" />
                          <div className="relative z-10">
                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                              <Flag className="w-6 h-6 text-slate-300 dark:text-slate-650" />
                            </div>
                            <h4 className="text-xl font-serif font-black text-slate-800 dark:text-white mb-2 tracking-tight">Ledger Clear</h4>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium max-w-xs mx-auto">No community posts have been flagged by students or alumni.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {reportedPosts.map((post) => (
                            <div key={post.id} className="bg-white dark:bg-slate-900 rounded-[1.8rem] border border-slate-200/60 dark:border-slate-800/85 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                              <div className="absolute top-0 left-0 h-full w-1 bg-rose-500" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider flex items-center gap-1">
                                    <Flag className="w-3 h-3" /> Flagged {post.flags_count} Times
                                  </span>
                                  <span className="text-slate-400 dark:text-slate-500 text-[10px] font-bold font-mono">Author ID: {post.author?.id}</span>
                                </div>
                                <h4 className="text-slate-800 dark:text-slate-200 text-base leading-relaxed mb-3 font-semibold italic tracking-tight">
                                  "{post.content}"
                                </h4>
                                <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                  <span>Author: <strong className="text-[#002147] dark:text-slate-200">{post.author?.name}</strong></span>
                                </div>
                              </div>

                              <Button 
                                onClick={() => handleDeletePost(post.id)}
                                className="w-full md:w-auto bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 h-10 rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-rose-500/10 text-xs"
                              >
                                <Trash2 className="w-4 h-4" /> Soft-Delete Post
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: PROFILE REPORTS */}
                  {activeTab === "reports" && (
                    <div className="space-y-4">
                      {profileReports.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-16 text-center border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-50/50 dark:bg-blue-500/5 rounded-full blur-2xl" />
                          <div className="relative z-10">
                            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                              <ShieldAlert className="w-6 h-6 text-slate-300 dark:text-slate-650" />
                            </div>
                            <h4 className="text-xl font-serif font-black text-slate-800 dark:text-white mb-2 tracking-tight">Ledger Clear</h4>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium max-w-xs mx-auto">No false information reports actively logged against alumni profiles.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {profileReports.map((report) => (
                            <div key={report.report_id} className="bg-white dark:bg-slate-900 rounded-[1.8rem] border border-slate-200/60 dark:border-slate-800/85 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                              <div className="absolute top-0 left-0 h-full w-1 bg-amber-500" />
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <span className="bg-amber-50 text-amber-705 dark:bg-amber-500/10 dark:text-amber-400 px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider">
                                    False Info Report
                                  </span>
                                  <span className="text-slate-400 dark:text-slate-500 text-[10px] font-bold font-mono">Report ID: {report.report_id}</span>
                                </div>

                                <div className="bg-slate-50 dark:bg-slate-950/30 rounded-2xl p-4 mb-4 border border-slate-100 dark:border-slate-850">
                                  <p className="text-[8px] text-slate-400 font-black uppercase tracking-wider mb-1">Reason / details</p>
                                  <p className="text-slate-700 dark:text-slate-350 text-xs font-semibold leading-relaxed italic">
                                    "{report.reason}"
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[10px] font-black uppercase tracking-wider pt-2 border-t border-slate-100 dark:border-slate-800">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-slate-450 text-[8px] tracking-wider font-black uppercase">Reporter:</span>
                                    <span className="text-[#002147] dark:text-slate-200 font-bold">{report.reporter?.name} ({report.reporter?.email})</span>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-slate-455 text-[8px] tracking-wider font-black uppercase">Target Alumni:</span>
                                    <span className="text-rose-500 font-bold">{report.target?.name} ({report.target?.email})</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                                <Button 
                                  onClick={() => handleResolveProfileReport(report.report_id, "strip_verification")}
                                  className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-10 px-4 rounded-xl flex items-center justify-center gap-1.5 text-xs shadow-md shadow-amber-500/10"
                                >
                                  <Lock className="w-3.5 h-3.5" /> Strip Verification
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleResolveProfileReport(report.report_id, "dismiss")}
                                  className="border-slate-200 dark:border-slate-850 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold h-10 px-4 rounded-xl flex items-center justify-center gap-1.5 text-xs"
                                >
                                  <X className="w-3.5 h-3.5" /> Dismiss Report
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: ALUMNI STATISTICS */}
                  {activeTab === "statistics" && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      {/* STATS OVERVIEW CARDS */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
                          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-bl-full pointer-events-none" />
                          <div className="flex items-center gap-3.5 mb-4">
                            <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                              <Award className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Registered Alumni</p>
                              <h4 className="text-2xl font-serif font-black text-slate-900 dark:text-white leading-none mt-1">
                                {stats?.total_alumni || 0}
                              </h4>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Verified platform mentors & members</p>
                        </div>

                        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
                          <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-bl-full pointer-events-none" />
                          <div className="flex items-center gap-3.5 mb-4">
                            <div className="p-2.5 rounded-xl bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                              <BookOpen className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Enrolled Students</p>
                              <h4 className="text-2xl font-serif font-black text-slate-900 dark:text-white leading-none mt-1">
                                {stats?.total_students || 0}
                              </h4>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Undergraduates seeking guidance</p>
                        </div>

                        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/80 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
                          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-bl-full pointer-events-none" />
                          <div className="flex items-center gap-3.5 mb-4">
                            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                              <MessageSquare className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Interceptions</p>
                              <h4 className="text-2xl font-serif font-black text-slate-900 dark:text-white leading-none mt-1">
                                {stats?.total_requests || mentorshipRequests.length}
                              </h4>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Total logged mentorship flows</p>
                        </div>
                      </div>

                      {/* AREA CHART BLOCK */}
                      <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                          <div>
                            <h3 className="text-xl font-serif font-bold text-[#002147] dark:text-white tracking-tight flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-blue-500" />
                              Alumni Growth Over Time
                            </h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium mt-0.5">Interactive registration projection & historic flow index</p>
                          </div>
                          <div className="px-3.5 py-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] text-blue-600 dark:text-blue-400">
                            Sync Live
                          </div>
                        </div>

                        <div className="w-full h-[320px] -ml-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendsData}>
                              <defs>
                                <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#1e293b' : '#f1f5f9'} />
                              <XAxis 
                                dataKey="year" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 11, fontWeight: 'bold' }} 
                              />
                              <YAxis 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 11, fontWeight: 'bold' }} 
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <div className="bg-slate-950/90 dark:bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Year {payload[0].payload.year}</p>
                                        <p className="text-xl font-serif font-black text-white mt-1">
                                          {payload[0].value} <span className="text-xs font-sans font-bold text-blue-400 uppercase tracking-wide">Alumni</span>
                                        </p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="count"
                                stroke="#3b82f6"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorGrowth)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* EMPLOYMENT & HIRING DISTRIBUTION */}
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        {/* Employment Status Pie */}
                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <h3 className="text-lg font-serif font-bold text-[#002147] dark:text-white mb-6 tracking-tight flex items-center gap-2">
                            <Activity className="w-5 h-5 text-teal-500" />
                            Alumni Employment Status
                          </h3>
                          <div className="w-full h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={statusData.length > 0 ? statusData : [
                                    { status: "Employed", count: 65 },
                                    { status: "Higher Studies", count: 20 },
                                    { status: "Entrepreneurship", count: 15 }
                                  ]}
                                  dataKey="count"
                                  nameKey="status"
                                  innerRadius={70}
                                  outerRadius={100}
                                  paddingAngle={4}
                                >
                                  {(statusData.length > 0 ? statusData : [{}, {}, {}]).map((_, index) => (
                                    <Cell key={index} fill={["#3b82f6", "#14b8a6", "#f59e0b"][index % 3]} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-slate-950/90 dark:bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{payload[0].name}</p>
                                          <p className="text-xl font-serif font-black text-white mt-1">
                                            {payload[0].value}% <span className="text-xs font-sans font-bold text-teal-400 uppercase tracking-wide">Share</span>
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="flex justify-center gap-6 mt-2 text-[10px] font-black uppercase tracking-wider text-slate-400">
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Employed</span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal-500" /> Higher Ed</span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Startup</span>
                          </div>
                        </div>

                        {/* Top Hiring Companies */}
                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <h3 className="text-lg font-serif font-bold text-[#002147] dark:text-white mb-6 tracking-tight flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-indigo-500" />
                            Top Hiring Companies
                          </h3>
                          <div className="w-full h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={topEmployers.length > 0 ? topEmployers : [
                                { company: "Google", count: 12 },
                                { company: "Microsoft", count: 8 },
                                { company: "Amazon", count: 7 },
                                { company: "Meta", count: 5 },
                                { company: "Netflix", count: 4 }
                              ]}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#1e293b' : '#f1f5f9'} />
                                <XAxis 
                                  dataKey="company" 
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <YAxis 
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-slate-950/90 dark:bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{payload[0].payload.company}</p>
                                          <p className="text-xl font-serif font-black text-white mt-1">
                                            {payload[0].value} <span className="text-xs font-sans font-bold text-indigo-455 uppercase tracking-wide">Hired</span>
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar dataKey="count" fill="#6366f1" radius={[8, 8, 0, 0]} barSize={28} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      {/* INDUSTRY & ROLE DEMOGRAPHICS */}
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        {/* Domain Distribution Vertical Bar */}
                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <h3 className="text-lg font-serif font-bold text-[#002147] dark:text-white mb-6 tracking-tight flex items-center gap-2">
                            <Globe className="w-5 h-5 text-amber-500" />
                            Top Industry Domains
                          </h3>
                          <div className="w-full h-[320px] -ml-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart 
                                data={domains.length > 0 ? domains : [
                                  { domain: "Software Dev", count: 45 },
                                  { domain: "Data Science", count: 28 },
                                  { domain: "FinTech", count: 18 },
                                  { domain: "Cybersecurity", count: 12 },
                                  { domain: "AI / ML", count: 9 }
                                ]} 
                                layout="vertical"
                              >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme === 'dark' ? '#1e293b' : '#f1f5f9'} />
                                <XAxis 
                                  type="number" 
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <YAxis 
                                  dataKey="domain" 
                                  type="category" 
                                  width={110}
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-slate-950/90 dark:bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{payload[0].payload.domain}</p>
                                          <p className="text-xl font-serif font-black text-white mt-1">
                                            {payload[0].value} <span className="text-xs font-sans font-bold text-amber-400 uppercase tracking-wide">Alumni</span>
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar dataKey="count" fill="#f59e0b" radius={[0, 8, 8, 0]} barSize={16} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Role Distribution Bar */}
                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 border border-slate-200/60 dark:border-slate-800/80 shadow-sm relative overflow-hidden">
                          <h3 className="text-lg font-serif font-bold text-[#002147] dark:text-white mb-6 tracking-tight flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-blue-500" />
                            Job Role Distribution
                          </h3>
                          <div className="w-full h-[320px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={roles.length > 0 ? roles : [
                                { role: "SDE", count: 32 },
                                { role: "Product Manager", count: 18 },
                                { role: "Data Analyst", count: 14 },
                                { role: "HR Specialist", count: 9 },
                                { role: "UX Designer", count: 7 }
                              ]}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#1e293b' : '#f1f5f9'} />
                                <XAxis 
                                  dataKey="role" 
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <YAxis 
                                  axisLine={false} 
                                  tickLine={false}
                                  tick={{ fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-slate-950/90 dark:bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl backdrop-blur-md">
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{payload[0].payload.role}</p>
                                          <p className="text-xl font-serif font-black text-white mt-1">
                                            {payload[0].value} <span className="text-xs font-sans font-bold text-blue-400 uppercase tracking-wide">Alumni</span>
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} barSize={26} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </motion.div>
              </AnimatePresence>
            </div>

          </div>

        </div>

      </main>
    </div>
  );
};

export default AdminDashboard;
